import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { customInstance } from "@repo/api-client";
import { z } from "zod";

/**
 * The typed client for the ANONYMOUS waitlist live-status endpoints (waitlist-live-status), all under
 * `/api/public/v1/venues/{venueId}/waitlist/status/…`. Same uncredentialed `customInstance` transport +
 * zod validation as `booking/publicApi.ts`. The `{token}` is a per-entry HMAC bearer credential; the
 * response carries ONLY the entry's own position/party/estimate/state — never contact PII (R5).
 *
 * Codegen note: hand-typed against the landed controller wire shapes (like the public booking client),
 * migratable to the generated hooks with no call-site change once the spec is regenerated.
 */
const statusResponseSchema = z.object({
  /** false = the link is expired/tampered/unknown or past retention → the generic "no longer active" page. */
  active: z.boolean(),
  externalEntryId: z.string().nullable().optional(),
  venueName: z.string().nullable().optional(),
  partySize: z.number().nullable().optional(),
  /** The raw §C.9 state; the UI maps it 1:1 onto guest labels (`NOTIFIED` → "table ready"). */
  state: z.string().nullable().optional(),
  googleState: z.string().nullable().optional(),
  partiesAhead: z.number().nullable().optional(),
  estimatedWaitMinutes: z.number().nullable().optional(),
  canCancel: z.boolean(),
  version: z.number().nullable().optional(),
});
export type WaitlistStatus = z.infer<typeof statusResponseSchema>;

const base = (venueId: string) =>
  `/api/public/v1/venues/${encodeURIComponent(venueId)}/waitlist/status`;

export async function fetchWaitlistStatus(venueId: string, token: string): Promise<WaitlistStatus> {
  const body = await customInstance<unknown>(`${base(venueId)}/${encodeURIComponent(token)}`, {
    method: "GET",
  });
  return statusResponseSchema.parse(body);
}

/**
 * Cancel from the status page (R4) — CAS on the loaded [expectedVersion]. A lost CAS surfaces as a 409
 * `STALE_WRITE` (thrown by the mutator); the caller refetches and re-renders the authoritative state.
 */
export async function cancelWaitlistEntry(
  venueId: string,
  token: string,
  expectedVersion: number,
): Promise<WaitlistStatus> {
  const body = await customInstance<unknown>(
    `${base(venueId)}/${encodeURIComponent(token)}/cancel`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ expectedVersion }),
    },
  );
  return statusResponseSchema.parse(body);
}

/**
 * Poll the live status while the entry is still active. Refresh-on-open + a modest poll interval is the
 * graceful fallback the spec leads with; the per-entry SSE push stream is a deferred enhancement.
 */
export function useWaitlistStatus(venueId: string, token: string): UseQueryResult<WaitlistStatus> {
  return useQuery({
    queryKey: ["waitlist-status", venueId, token],
    queryFn: () => fetchWaitlistStatus(venueId, token),
    enabled: Boolean(venueId) && Boolean(token),
    refetchInterval: (query) => (query.state.data?.active ? 20_000 : false),
    refetchOnWindowFocus: true,
  });
}
